import { useCallback } from 'react'
import styled, { css } from 'styled-components'
import { desktopCSS } from '@measures/responsive'
import translate from '@i18n'
import { type Image } from '@utils/cook/types'
import { TrackingFnType } from '@hooks/useTracking'
import useTracking from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'

import BackgroundImage from './BackgroundImage'
import Label from './Label'
import Title from './Title'
import LogosImage from './Image'

const Wrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    height: 100%;
    position: relative;
    overflow: hidden;

    border-radius: ${spacing8};
    cursor: pointer;
  `}
`

const ContentWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8, spacing16 },
    },
  }) => css`
    position: relative;
    height: 100%;
    width: 100%;
    box-sizing: border-box;

    padding: 0 ${spacing16};

    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    gap: ${spacing8};

    ${desktopCSS(css`
      gap: ${spacing16};
    `)}
  `}
`

interface FollowMyTeamOverviewBannerProps {
  backgroundImage: Image<'16_9'>
  image: Image<'free'>
  title: string
  onClick: () => void
  state: 'notLoggedIn' | 'noPreferences'
}

const FollowMyTeamOverviewBanner = ({
  backgroundImage,
  image,
  title,
  state,
  onClick,
}: FollowMyTeamOverviewBannerProps) => {
  const onImpressionEvent = useCallback<TrackingFnType>(
    () => ({
      event: 'element_impression',
      element: 'fmt_banner',
      element_state: state,
      targetContentType: 'section',
    }),
    [state]
  )

  const onClickEvent = useCallback<TrackingFnType>(
    () => ({
      event: 'element_click',
      element: 'fmt_banner',
      element_state: state,
      targetContentType: 'section',
    }),
    [state]
  )

  const trackedOnImpression = useTracking(onImpressionEvent)
  const trackedOnClick = useTracking(onClickEvent)

  const viewportRef = useViewportTracking({
    track: true,
    onImpression: trackedOnImpression,
  })

  const handleClick = useCallback(() => {
    onClick()
    trackedOnClick()
  }, [trackedOnClick, onClick])

  return (
    <Wrapper ref={viewportRef} onClick={handleClick}>
      <BackgroundImage image={backgroundImage}>
        <ContentWrapper>
          <Title>
            <Label>{translate('followMyTeamOverview.new')}</Label>
            {title}
          </Title>
          <LogosImage image={image} />
        </ContentWrapper>
      </BackgroundImage>
    </Wrapper>
  )
}

export default FollowMyTeamOverviewBanner
